import { useSyncExternalStore } from 'react';

/**
 * Where the session lives while the app is running (`ARCH-003 §1.1`).
 *
 * **The access token is a module variable and nothing else.** Not `localStorage`, not `sessionStorage`,
 * not a cookie this script can read: each of those is readable by script and survives the tab, so any XSS
 * on the page becomes a stolen session that outlives the page. The refresh token is in an `HttpOnly;
 * Secure; SameSite=Strict` cookie the server sets, which this code cannot read *by design* — and cannot
 * assert about either, which is why the test that proves those flags is an API test rather than one here.
 *
 * A reload therefore starts with no access token and asks for one. That is the whole reason `status`
 * begins as `restoring` rather than `signed-out`: the two are not the same, and treating them the same
 * would bounce every reload to the welcome screen for the length of one request.
 */
export type SessionStatus = 'restoring' | 'signed-in' | 'signed-out';

export interface Session {
  readonly status: SessionStatus;
  /** Null in every state but `signed-in`. */
  readonly accessToken: string | null;
  /** `FEAT-003 §5.3` re-reads this on each refresh, so it is never carried forward stale. */
  readonly emailVerified: boolean;
}

const RESTORING: Session = { status: 'restoring', accessToken: null, emailVerified: false };
const SIGNED_OUT: Session = { status: 'signed-out', accessToken: null, emailVerified: false };

let current: Session = RESTORING;
const listeners = new Set<() => void>();

/** The session as it stands. Read at call time, never captured. */
export function getSession(): Session {
  return current;
}

/** The access token, or null. The API client asks for this on every request rather than holding one. */
export function getAccessToken(): string | null {
  return current.accessToken;
}

/** Records a session. Called by sign-in and by every successful refresh. */
export function setSession(session: { accessToken: string; emailVerified: boolean }): void {
  publish({ status: 'signed-in', accessToken: session.accessToken, emailVerified: session.emailVerified });
}

/** Forgets the session. Called when a refresh is refused, and on sign-out. */
export function clearSession(): void {
  publish(SIGNED_OUT);
}

/** Back to the state a fresh page load starts in. Tests use it; the app has no reason to. */
export function resetSessionForTests(): void {
  publish(RESTORING);
}

/** Subscribes to changes. Returns the unsubscribe. */
export function subscribeToSession(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * The session, as a hook. `useSyncExternalStore` rather than a context provider: the store has to be
 * readable from the API client, which is not inside the React tree at all, and a value that lives in two
 * places is a value that disagrees with itself.
 */
export function useSession(): Session {
  return useSyncExternalStore(subscribeToSession, getSession, getSession);
}

function publish(next: Session): void {
  current = next;
  for (const listener of listeners) {
    listener();
  }
}
